import React, { useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Switch,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Statistic,
  Upload,
  Image,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  UploadOutlined,
  EyeOutlined,
  CalendarOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { getStorageUrl } from '../utils/storageUtils';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Blog = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const queryClient = useQueryClient();

  // Fetch blog posts with filters
  const { data: posts = [], isLoading } = useQuery(
    ['blog', searchTerm, categoryFilter],
    async () => {
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (categoryFilter) params.append('category', categoryFilter);

        const response = await api.get(`/admin/blog?${params.toString()}`);
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch blog posts:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 30000,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Client-side filtering fallback
  const filteredPosts = useMemo(() => {
    if (!Array.isArray(posts)) return [];
    let filtered = [...posts];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          (p.title && p.title.toLowerCase().includes(searchLower)) ||
          (p.author && p.author.toLowerCase().includes(searchLower)),
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter((p) => p.category === categoryFilter);
    }

    return filtered;
  }, [posts, searchTerm, categoryFilter]);

  // Create blog post mutation
  const createPostMutation = useMutation(
    async (postData) => {
      const formData = new FormData();

      Object.keys(postData).forEach((key) => {
        if (key !== 'image' && postData[key] !== undefined) {
          formData.append(key, postData[key]);
        }
      });

      const fileObj = Array.isArray(postData.image) ? postData.image[0] : postData.image;
      if (fileObj?.originFileObj) {
        formData.append('image', fileObj.originFileObj);
      }

      const response = await api.post('/admin/blog', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Blog post created successfully');
        setIsModalVisible(false);
        form.resetFields();
        setPreviewImage(null);
        queryClient.invalidateQueries('blog');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to create blog post');
      },
    },
  );

  // Update blog post mutation
  const updatePostMutation = useMutation(
    async ({ id, ...postData }) => {
      const formData = new FormData();

      Object.keys(postData).forEach((key) => {
        if (key !== 'image' && postData[key] !== undefined) {
          formData.append(key, postData[key]);
        }
      });

      const fileObj = Array.isArray(postData.image) ? postData.image[0] : postData.image;
      if (fileObj?.originFileObj) {
        formData.append('image', fileObj.originFileObj);
      }

      formData.append('_method', 'PUT');

      const response = await api.post(`/admin/blog/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Blog post updated successfully');
        setIsModalVisible(false);
        setEditingPost(null);
        form.resetFields();
        setPreviewImage(null);
        queryClient.invalidateQueries('blog');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update blog post');
      },
    },
  );

  // Delete blog post mutation
  const deletePostMutation = useMutation(
    async (id) => {
      await api.delete(`/admin/blog/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Blog post deleted successfully');
        queryClient.invalidateQueries('blog');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete blog post');
      },
    },
  );

  const handleSubmit = async (values) => {
    try {
      if (editingPost) {
        updatePostMutation.mutate({ id: editingPost.id, ...values });
      } else {
        createPostMutation.mutate(values);
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    form.setFieldsValue({
      ...post,
      is_featured: post.is_featured ?? false,
      is_published: post.is_published ?? false,
    });
    setPreviewImage(post.image_url);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    deletePostMutation.mutate(id);
  };

  const uploadProps = {
    name: 'image',
    listType: 'picture-card',
    className: 'avatar-uploader',
    showUploadList: false,
    beforeUpload: (file) => {
      const isJpgOrPng =
        file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG/PNG/WEBP file!');
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must smaller than 2MB!');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange: (info) => {
      if (info.file.status === 'removed') {
        if (previewImage && previewImage.startsWith('blob:')) {
          URL.revokeObjectURL(previewImage);
        }
        setPreviewImage(null);
        form.setFieldsValue({ image: null });
      } else if (info.file.originFileObj) {
        if (previewImage && previewImage.startsWith('blob:')) {
          URL.revokeObjectURL(previewImage);
        }
        setPreviewImage(URL.createObjectURL(info.file.originFileObj));
      }
    },
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      render: (title) => (
        <Tooltip title={title}>
          <span>{title}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      sorter: (a, b) => (a.author || '').localeCompare(b.author || ''),
      render: (author) => <span>{author}</span>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
      render: (category) => (
        <Tag color="blue">{category?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Read Time',
      dataIndex: 'read_time',
      key: 'read_time',
      render: (readTime) => <span>{readTime || '-'}</span>,
    },
    {
      title: 'Featured',
      dataIndex: 'is_featured',
      key: 'is_featured',
      render: (isFeatured) => (
        <Tag color={isFeatured ? 'gold' : 'default'}>
          {isFeatured ? 'FEATURED' : 'NO'}
        </Tag>
      ),
    },
    {
      title: 'Published',
      dataIndex: 'is_published',
      key: 'is_published',
      render: (isPublished) => (
        <Tag color={isPublished ? 'green' : 'red'}>
          {isPublished ? 'PUBLISHED' : 'DRAFT'}
        </Tag>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (createdAt) => (
        <span>{createdAt ? dayjs(createdAt).format('MMM DD, YYYY') : '-'}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this blog post?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deletePostMutation.isLoading}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Calculate statistics
  const totalPosts = posts.length;
  const publishedPosts = posts.filter((p) => p.is_published).length;
  const featuredPosts = posts.filter((p) => p.is_featured).length;

  return (
    <div>
      <Title level={2}>
        <FileTextOutlined /> Blog Posts Management
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Total Posts"
              value={totalPosts}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Published"
              value={publishedPosts}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Featured"
              value={featuredPosts}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* Filters and Actions */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search by title or author..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="news">News</Option>
              <Option value="tips">Tips</Option>
              <Option value="guides">Guides</Option>
              <Option value="updates">Updates</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={10} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingPost(null);
                form.resetFields();
                setPreviewImage(null);
                setIsModalVisible(true);
              }}
            >
              Add New Post
            </Button>
          </Col>
        </Row>

        {/* Blog Posts Table */}
        <Table
          columns={columns}
          dataSource={filteredPosts}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: filteredPosts.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} posts`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Add/Edit Blog Post Modal */}
      <Modal
        title={editingPost ? 'Edit Blog Post' : 'Add New Blog Post'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingPost(null);
          form.resetFields();
          setPreviewImage(null);
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter post title' }]}
          >
            <Input placeholder="Enter post title" maxLength={200} />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="author"
                label="Author"
                rules={[{ required: true, message: 'Please enter author name' }]}
              >
                <Input placeholder="Enter author name" maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select placeholder="Select category">
                  <Option value="news">News</Option>
                  <Option value="tips">Tips</Option>
                  <Option value="guides">Guides</Option>
                  <Option value="updates">Updates</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="read_time" label="Read Time">
                <Input placeholder="e.g. 5 min read" maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="image_url" label="Image URL (Optional)">
                <Input placeholder="https://example.com/image.jpg" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="excerpt"
            label="Excerpt"
            rules={[{ required: true, message: 'Please enter post excerpt' }]}
          >
            <TextArea rows={2} placeholder="Enter post excerpt" maxLength={500} showCount />
          </Form.Item>

          <Form.Item
            name="content"
            label="Content"
            rules={[{ required: true, message: 'Please enter post content' }]}
          >
            <TextArea rows={8} placeholder="Enter post content" />
          </Form.Item>

          <Form.Item name="image" label="Featured Image" valuePropName="file">
            <Upload {...uploadProps} customRequest={({ onSuccess }) => onSuccess('ok')}>
              {previewImage ? (
                <img
                  src={getStorageUrl(previewImage)}
                  alt="blog"
                  style={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: 200,
                    backgroundColor: '#fafafa',
                    border: '1px dashed #d9d9d9',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <UploadOutlined
                    style={{ fontSize: '34px', color: '#d9d9d9', marginBottom: '8px' }}
                  />
                  <div style={{ color: '#666' }}>Click or drag image to upload</div>
                  <div style={{ color: '#999', fontSize: '14px', marginTop: '4px' }}>
                    JPG, PNG, WEBP (Max 2MB)
                  </div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="is_featured" label="Featured" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="is_published" label="Published" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingPost(null);
                  form.resetFields();
                  setPreviewImage(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createPostMutation.isLoading || updatePostMutation.isLoading}
              >
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Blog;
